import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowDown, ArrowRight, ScanFace, FileText } from "lucide-react";
import KineticHeadline from "../components/KineticHeadline";
import Marquee from "../components/Marquee";
import Reveal from "../components/Reveal";
import FigureBlock from "../components/FigureBlock";
import Redacted from "../components/Redacted";
import { NAV, STATS, MEMBERS } from "../data/site";

const HERO_IMG =
    "https://images.unsplash.com/photo-1573358695783-005699a7fc94?q=80&w=2000&auto=format&fit=crop";

function Reticle() {
    return (
        <div className="absolute top-[14%] left-[52%] sm:left-[56%] w-36 sm:w-56 pointer-events-none" aria-hidden="true">
            <div className="relative w-full aspect-[4/3]">
                <span className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-amber" />
                <span className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-amber" />
                <span className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-amber" />
                <span className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-amber" />
                <div className="absolute inset-x-0 h-px bg-amber/70 animate-scan-sweep" />
                <span className="absolute -bottom-6 left-0 font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-amber bg-ink/70 px-2 py-0.5 border border-amber/30">
                    Match 99.2% · Subject: everyone
                </span>
            </div>
        </div>
    );
}

export default function Home() {
    const indexRef = useRef(null);
    const scrollToIndex = () => {
        const el = indexRef.current;
        if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
        else el?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <div>
            {/* HERO */}
            <section className="relative min-h-screen flex flex-col justify-end overflow-hidden border-b border-white/10">
                <div className="absolute inset-0">
                    <motion.img
                        src={HERO_IMG}
                        alt="A dense crowd of people walking along a busy city street, their faces caught in motion"
                        className="w-full h-full object-cover img-treat"
                        initial={{ scale: 1.12, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 2.2, ease: [0.16, 1, 0.3, 1] }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-ink/85 via-ink/55 to-ink" />
                    <div className="absolute inset-0 scanlines" />
                    <Reticle />
                </div>

                <div className="relative mx-auto w-full max-w-7xl px-5 sm:px-8 pb-16 sm:pb-24 pt-32">
                    <Reveal delay={0.15} y={12} className="mb-6">
                        <p className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.3em] text-amber flex flex-wrap items-center gap-3">
                            <span className="border border-amber/40 px-2 py-1">FILE NO. FR-NZ-00</span>
                            <span>COMP501 · RESEARCH DOSSIER · AOTEAROA NEW ZEALAND</span>
                        </p>
                    </Reveal>

                    <KineticHeadline
                        lines={["The police", "can now read", <>your <i className="text-amber">face</i>.</>]}
                        className="font-display font-medium text-paper leading-[0.98] text-[clamp(3rem,10vw,8.5rem)] max-w-6xl"
                    />

                    <Reveal delay={0.55} y={20}>
                        <p className="mt-8 max-w-2xl text-base sm:text-lg leading-relaxed text-paper/80">
                            Facial Recognition Technology is already inside policing — trialled, tested and quietly
                            retired. Our research examines how it works, what it could deliver, what goes wrong when
                            it errs, and the three governance choices New Zealand now faces.
                        </p>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <button
                                onClick={scrollToIndex}
                                data-testid="hero-begin-reading-button"
                                className="group flex items-center gap-3 bg-amber text-ink px-6 py-3.5 font-mono text-xs uppercase tracking-[0.25em] hover:bg-paper transition-colors"
                            >
                                Begin reading
                                <ArrowDown size={14} className="group-hover:translate-y-0.5 transition-transform" />
                            </button>
                            <Link
                                to="/governance-options"
                                data-testid="hero-governance-link"
                                className="flex items-center gap-3 border border-paper/30 text-paper px-6 py-3.5 font-mono text-xs uppercase tracking-[0.25em] hover:border-amber hover:text-amber transition-colors"
                            >
                                The three choices
                                <ArrowRight size={14} />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            <Marquee
                items={[
                    "Biometric surveillance",
                    "Legislative lag",
                    "False positives",
                    "The chilling effect",
                    "Human verification",
                    "Independent oversight",
                    "Temporary restriction",
                    "Public trust",
                ]}
            />

            {/* STAT BAND */}
            <section className="border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 grid sm:grid-cols-2 lg:grid-cols-4">
                    {STATS.map((s, i) => (
                        <Reveal
                            key={i}
                            delay={i * 0.08}
                            className={`border-white/10 p-8 sm:p-10 ${i % 2 === 1 ? "sm:border-l" : ""} ${i > 1 ? "sm:border-t lg:border-t-0" : ""} ${i > 0 ? "lg:border-l" : ""}`}
                        >
                            <p className="font-display text-4xl sm:text-5xl text-amber" data-testid={`stat-value-${i}`}>
                                {s.value}
                            </p>
                            <p className="mt-4 text-sm leading-relaxed text-ash">{s.label}</p>
                            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-ash/70">
                                Source: {s.cite}
                            </p>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* CLASSIFIED STRIP */}
            <section className="border-b border-white/10 bg-sheet">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-20 text-center sm:text-left sm:flex items-baseline justify-between gap-8">
                    <Reveal>
                        <p className="font-display text-2xl sm:text-4xl text-paper leading-snug max-w-3xl">
                            Much of what Police tested was <Redacted>revealed only through Official Information
                            Act requests and media exposure</Redacted> — a reminder that surveillance grows quietly
                            unless it is watched.
                        </p>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ash mt-6 sm:mt-0 shrink-0">
                            Hover to declassify
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* SITE INDEX */}
            <section ref={indexRef} className="border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
                    <Reveal className="mb-10 flex items-baseline justify-between gap-6">
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber mb-3">
                                Dossier index
                            </p>
                            <h2 className="font-display text-3xl sm:text-5xl text-paper">Ten files, one question.</h2>
                        </div>
                        <p className="hidden sm:block font-mono text-[11px] text-ash max-w-[220px] text-right leading-relaxed">
                            Read in order, or jump straight to the governance choices.
                        </p>
                    </Reveal>
                    <ul>
                        {NAV.filter((n) => n.no !== "00" && !n.child).map((item, i) => (
                            <Reveal key={item.to} delay={i * 0.04} y={16}>
                                <li>
                                    <Link
                                        to={item.to}
                                        data-testid={`home-index-link-${item.no}`}
                                        className="group flex items-baseline gap-4 sm:gap-8 border-t border-white/10 py-5 sm:py-6 hover:bg-amber/[0.04] transition-colors px-2 -mx-2"
                                    >
                                        <span className="font-mono text-xs text-amber w-10">{item.no}</span>
                                        <span className="font-display text-xl sm:text-3xl text-paper group-hover:text-amber group-hover:translate-x-1 transition-all duration-300">
                                            {item.label}
                                        </span>
                                        <ArrowRight
                                            size={18}
                                            className="ml-auto text-ash opacity-0 group-hover:opacity-100 group-hover:text-amber group-hover:translate-x-1 transition-all duration-300"
                                        />
                                    </Link>
                                </li>
                            </Reveal>
                        ))}
                    </ul>
                </div>
            </section>

            {/* ROLES + FIGURE */}
            <section className="bg-sheet border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
                    <div>
                        <Reveal>
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber mb-3">The group</p>
                            <h2 className="font-display text-3xl sm:text-4xl text-paper mb-10">
                                Three roles, one dossier.
                            </h2>
                        </Reveal>
                        <div className="space-y-6">
                            {MEMBERS.map((m, i) => (
                                <Reveal key={m.name} delay={i * 0.08}>
                                    <div className="border border-white/10 p-6 hover:border-amber/50 transition-colors" data-testid={`home-role-card-${m.name.toLowerCase()}`}>
                                        <div className="flex items-center justify-between gap-4 mb-2">
                                            <p className="font-display text-2xl text-paper">{m.name}</p>
                                            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber border border-amber/40 px-2 py-1">
                                                {m.role}
                                            </span>
                                        </div>
                                        <p className="text-sm leading-relaxed text-ash">{m.responsibilities}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                    <div className="lg:pt-16">
                        <FigureBlock
                            num="1"
                            src="https://images.unsplash.com/photo-1618482914248-29272d021005?q=80&w=1600&auto=format&fit=crop"
                            alt="Multi-lens surveillance camera array mounted on a pole"
                            caption="Multi-lens CCTV arrays like this one can stream hours of public-space footage into recognition software. The camera never blinks — the question is who, and what rules, decide what it looks for."
                            ratio="4/3"
                        />
                        <Reveal delay={0.1} className="mt-8">
                            <Link
                                to="/what-is-facial-recognition"
                                data-testid="home-start-file-01-link"
                                className="inline-flex items-center gap-3 text-amber font-mono text-xs uppercase tracking-[0.25em] border-b border-amber/40 pb-2 hover:gap-5 transition-all"
                            >
                                <ScanFace size={15} /> Start with file 01 — What is Facial Recognition?
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* METHOD NOTE */}
            <section className="border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-20 flex flex-col sm:flex-row gap-8 items-start">
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ash border border-white/15 px-3 py-2 flex items-center gap-2 shrink-0">
                        <FileText size={13} /> Method note
                    </span>
                    <Reveal>
                        <p className="max-w-4xl text-base sm:text-lg leading-relaxed text-ash">
                            This dossier draws on peer-reviewed accuracy studies (NIST, 2019; Buolamwini &amp; Gebru,
                            2018), official New Zealand sources — the Independent Police Conduct Authority (2020) and
                            the Office of the Privacy Commissioner (2024, 2025) — plus case law and reputable
                            journalism. Every claim is cited in APA style on the{" "}
                            <Link to="/references" className="text-amber underline decoration-amber/40 underline-offset-4 hover:decoration-amber" data-testid="home-method-references-link">
                                References page
                            </Link>
                            .
                        </p>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
